import { useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Patient, Encounter, AllergyIntolerance } from '@medplum/fhirtypes';
import { prepareAllergyForSave } from '@/utils/allergy.utils';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { HL7 } from '@/consts/systems.consts';

export interface UseAllergyActionsReturn {
  /**
   * Save an allergy to the server (create or update).
   * Returns the saved allergy with server-assigned ID.
   */
  saveAllergy: (allergy: AllergyIntolerance) => Promise<AllergyIntolerance>;

  /**
   * Soft delete an allergy (mark as entered-in-error).
   * This preserves the audit trail while removing from active view.
   */
  deleteAllergy: (id: string) => Promise<void>;
}

/**
 * Hook for managing allergy server operations.
 * Centralizes all FHIR AllergyIntolerance create/update/delete operations.
 *
 * @param patient - The patient resource
 * @param encounter - The current encounter resource
 * @returns Object with saveAllergy and deleteAllergy functions
 *
 * @example
 * ```tsx
 * const { saveAllergy, deleteAllergy } = useAllergyActions(patient, encounter);
 *
 * // Save a new allergy
 * const saved = await saveAllergy(newAllergy);
 *
 * // Delete (soft) an allergy
 * await deleteAllergy(allergyId);
 * ```
 */
export function useAllergyActions(
  patient: Patient,
  encounter: Encounter | undefined
): UseAllergyActionsReturn {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages']);

  const saveAllergy = useCallback(
    async (allergy: AllergyIntolerance): Promise<AllergyIntolerance> => {
      // Prepare the FHIR resource for saving
      if (!encounter) {
        notifications.show({
          color: 'red',
          title: t('summaryPage.summary.allergies.encounterRequired', {
            defaultValue: 'Encounter is required to add an allergy',
          }),
          message: t('summaryPage.summary.allergies.encounterRequiredMessage', {
            defaultValue: 'Please create an encounter first',
          }),
        });
        return Promise.reject(
          new Error('Encounter is required to add an allergy')
        );
      }
      const resource = prepareAllergyForSave(patient, encounter, allergy);

      // Create or update based on whether we have an ID
      const saved = allergy.id
        ? await medplum.updateResource(resource)
        : await medplum.createResource(resource);

      // Invalidate cache so subsequent reads get fresh data
      medplum.invalidateSearches('AllergyIntolerance');

      return saved;
    },
    [medplum, patient, encounter, t]
  );

  const deleteAllergy = useCallback(
    async (id: string): Promise<void> => {
      // Soft delete - mark as entered-in-error
      const existing = await medplum.readResource('AllergyIntolerance', id);
      await medplum.updateResource({
        ...existing,
        clinicalStatus: undefined,
        verificationStatus: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.ALLERGY_INTOLERANCE.VERIFICATION.BASE,
              code: HL7.TERMINOLOGY.CS.ALLERGY_INTOLERANCE.VERIFICATION.CODES
                .ENTERED_IN_ERROR,
            },
          ],
        },
      });

      // Invalidate cache
      medplum.invalidateSearches('AllergyIntolerance');
    },
    [medplum]
  );

  return { saveAllergy, deleteAllergy };
}
